import { useTranslation } from 'react-i18next';
import type { ChatSettings } from '../hooks/useRouterSettings';

export interface ChatBubbleProps {
  /** Click handler to toggle the chat window */
  onClick: () => void;
  /** Chat settings for customization (optional - uses defaults if not provided) */
  settings?: ChatSettings | null;
}

/**
 * Floating chat bubble button component.
 *
 * Supports 4 button styles controlled by settings.buttonStyle:
 * - "icon"        — 56px circle with chat SVG icon (default)
 * - "text"        — pill-shaped button with text
 * - "avatar_text" — pill with avatar image + text
 * - "avatar"      — 56px circle with the profile picture as background
 */
export function ChatBubble({ onClick, settings }: ChatBubbleProps) {
  const { t } = useTranslation('widget');
  const buttonColor = settings?.settings.primaryColor ?? '#3b82f6';
  const buttonStyle = settings?.settings.buttonStyle ?? 'icon';
  const profilePictureUrl = settings?.settings.profilePictureUrl ?? '';
  const buttonText = settings?.settings.buttonText || '';

  if (buttonStyle === 'text') {
    return (
      <button
        onClick={onClick}
        className="
          shadow-lg cursor-pointer border-0 outline-none
          transition-transform duration-200
          hover:scale-105 focus:scale-105
          focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500
        "
        style={{
          backgroundColor: buttonColor,
          color: '#ffffff',
          borderRadius: '28px',
          padding: '12px 20px',
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '1.4',
          maxWidth: '280px',
        }}
        aria-label={t('bubble.openChat')}
        type="button"
      >
        {buttonText || t('bubble.chatWithUs')}
      </button>
    );
  }

  if (buttonStyle === 'avatar_text') {
    return (
      <button
        onClick={onClick}
        className="
          shadow-lg cursor-pointer border-0 outline-none
          transition-transform duration-200
          hover:scale-105 focus:scale-105
          focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500
        "
        style={{
          backgroundColor: buttonColor,
          color: '#ffffff',
          borderRadius: '28px',
          padding: '8px 20px 8px 8px',
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '1.4',
          maxWidth: '320px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
        aria-label={t('bubble.openChat')}
        type="button"
      >
        {profilePictureUrl ? (
          <img
            src={profilePictureUrl}
            alt=""
            aria-hidden="true"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
        ) : (
          <div
            aria-hidden="true"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.2)',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ChatIcon size={20} />
          </div>
        )}
        <span>{buttonText || t('bubble.chatWithUs')}</span>
      </button>
    );
  }

  if (buttonStyle === 'avatar') {
    return (
      <button
        onClick={onClick}
        className="
          w-14 h-14 rounded-full shadow-lg
          flex items-center justify-center
          cursor-pointer border-0 outline-none
          transition-transform duration-200
          hover:scale-110 focus:scale-110
          focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500
        "
        style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          overflow: 'hidden',
          backgroundColor: buttonColor,
          padding: 0,
        }}
        aria-label={t('bubble.openChat')}
        type="button"
      >
        {profilePictureUrl ? (
          <img
            src={profilePictureUrl}
            alt=""
            aria-hidden="true"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <ChatIcon />
        )}
      </button>
    );
  }

  // Default: "icon" style — 56px circle with chat SVG icon
  return (
    <button
      onClick={onClick}
      className="
        w-14 h-14 rounded-full shadow-lg
        flex items-center justify-center
        cursor-pointer border-0 outline-none
        transition-transform duration-200
        hover:scale-110 focus:scale-110
        focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500
        animate-pulse-soft
      "
      style={{ backgroundColor: buttonColor }}
      aria-label={t('bubble.openChat')}
      type="button"
    >
      <ChatIcon />
    </button>
  );
}

/**
 * Default chat icon SVG component.
 * Uses a speech bubble icon that's universally recognized for chat.
 */
function ChatIcon({ size = 28 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      style={{ width: `${size}px`, height: `${size}px`, color: '#ffffff' }}
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97z"
        clipRule="evenodd"
      />
    </svg>
  );
}

export default ChatBubble;
